import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useIdeaPageContext } from './IdeaPageContext';
import { buildPerspectiveFiltersCacheKey } from '../../hooks/ideaPage/filterHelpers';
import {
  usePerspectiveConfigurations,
  useSinglePerspectiveCountQuery,
} from '../../hooks/ideaPage/useIdeaQueries';

const EMPTY_PERSPECTIVE_CONFIGURATIONS: any[] = [];
const PERSPECTIVE_FILTERS_DEBOUNCE_MS = 400;

const IdeaPagePerspectiveFetcher = (): null => {
  const {
    chosenKey,
    setChosenKey,
    activeFilters,
    trendFilters,
    setPerspectiveData,
    setPerspectiveDataLoading,
  } = useIdeaPageContext();
  const { id: ideaId } = useParams<{ id: string }>();
  const perspectiveConfigQuery = usePerspectiveConfigurations();
  const perspectiveConfigurations =
    perspectiveConfigQuery.data?.perspectiveConfigurations ?? EMPTY_PERSPECTIVE_CONFIGURATIONS;

  const perspectiveConfigKeys = useMemo(
    () => perspectiveConfigurations.map((config: any) => config.key).sort().join(','),
    [perspectiveConfigQuery.data]
  );

  const filtersKey = useMemo(
    () => buildPerspectiveFiltersCacheKey(activeFilters, trendFilters),
    [activeFilters, trendFilters]
  );
  const [debouncedFiltersKey, setDebouncedFiltersKey] = useState(filtersKey);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedFiltersKey(filtersKey);
    }, PERSPECTIVE_FILTERS_DEBOUNCE_MS);
    return () => clearTimeout(timeoutId);
  }, [filtersKey]);

  const filtersReadyForFetch = debouncedFiltersKey === filtersKey;

  useEffect(() => {
    if (!chosenKey && perspectiveConfigKeys) {
      setChosenKey('trend');
    }
  }, [chosenKey, perspectiveConfigKeys, setChosenKey]);

  const perspectiveCountQuery = useSinglePerspectiveCountQuery(
    ideaId,
    chosenKey,
    activeFilters,
    trendFilters,
    perspectiveConfigurations,
    { filtersCacheKey: debouncedFiltersKey, enabled: filtersReadyForFetch }
  );

  useEffect(() => {
    setPerspectiveDataLoading(
      perspectiveCountQuery.isLoading ||
        (perspectiveCountQuery.isFetching && !perspectiveCountQuery.data)
    );
  }, [
    perspectiveCountQuery.isLoading,
    perspectiveCountQuery.isFetching,
    perspectiveCountQuery.data,
    setPerspectiveDataLoading,
  ]);

  useEffect(() => {
    if (!chosenKey || !perspectiveCountQuery.data) {
      return;
    }
    setPerspectiveData((prev) => {
      if (prev[chosenKey] === perspectiveCountQuery.data) {
        return prev;
      }
      return { ...prev, [chosenKey]: perspectiveCountQuery.data };
    });
  }, [chosenKey, perspectiveCountQuery.data, setPerspectiveData]);

  useEffect(() => {
    if (!ideaId) {
      setPerspectiveData({});
    }
  }, [ideaId, setPerspectiveData]);

  return null;
};

export default IdeaPagePerspectiveFetcher;
